interface DateTabUpdatedEvent {
  type: 'date'
  minutes: number
  hours: number
  day: number
  month: number
  year: number
}
interface HourlyTabUpdatedEvent {
  type: 'hourly'
  minutes: number
  hourInterval: number
}
interface DailyTabUpdatedEvent {
  type: 'daily'
  minutes: number
  hours: number
  dayInterval: number
}
interface WeeklyTabUpdatedEvent {
  type: 'weekly'
  minutes: number
  hours: number
  days: string[]
}
interface MonthlyTabUpdatedEvent {
  type: 'monthly'
  minutes: number
  hours: number
  days: string[]
  monthInterval: number
}

interface CustomTabUpdatedEvent {
  type: 'custom'
  cronExpression: string
}

export type TabUpdatedEvent =
  | DateTabUpdatedEvent
  | HourlyTabUpdatedEvent
  | DailyTabUpdatedEvent
  | WeeklyTabUpdatedEvent
  | MonthlyTabUpdatedEvent
  | CustomTabUpdatedEvent

export type TabKey = TabUpdatedEvent[keyof TabUpdatedEvent]

export const buildExpression = (event: TabUpdatedEvent): string => {
  if (event.type === 'date') {
    return [
      event.minutes,
      event.hours,
      event.day,
      event.month,
      '?',
      event.year
    ].join(' ')
  }
  if (event.type === 'hourly') {
    return `${event.minutes} 0/${event.hourInterval} * * ? *`
  }
  if (event.type === 'daily') {
    return `${event.minutes} ${event.hours} 1/${event.dayInterval} * ? *`
  }
  if (event.type === 'weekly') {
    return (
      `${event.minutes} ${event.hours} ? * ` +
      `${event.days
        .filter(d => d)
        .sort()
        .join()}` +
      ` *`
    )
  }
  if (event.type === 'monthly') {
    return `${event.minutes} ${event.hours} ${event.days.join(',')} 1/${
      event.monthInterval
    } ? *`
  }
  if (event.type === 'custom') {
    return event.cronExpression
  }
  throw `unknown event type: ${event}`
}

export const parseExpression = (expression: string): TabUpdatedEvent => {
  if (expression.split(' ').length != 6) {
    return {
      type: 'custom',
      cronExpression: expression
    }
  }
  let groups = expression.match(/^(\d+) (\d+) (\d+) (\d+) \? (\d+)$/)
  if (groups !== null) {
    return {
      type: 'date',
      minutes: Number(groups[1]),
      hours: Number(groups[2]),
      day: Number(groups[3]),
      month: Number(groups[4]),
      year: Number(groups[5])
    }
  }
  groups = expression.match(/^(\d+) \*\/(\d+) \* \* \? \*$/)
  if (groups !== null) {
    return {
      type: 'hourly',
      minutes: Number(groups[1]),
      hourInterval: Number(groups[2])
    }
  }
  groups = expression.match(/^(\d+) (\d+) (\d+)\/(\d+) \* \? \*$/)
  if (groups !== null) {
    return {
      type: 'daily',
      minutes: Number(groups[1]),
      hours: Number(groups[2]),
      dayInterval: Number(groups[3])
    }
  }
  groups = expression.match(
    /^(\d+) (\d+) \? \* (\d)(,\d)?(,\d)?(,\d)?(,\d)?(,\d)?(,\d)? \*$/
  )
  if (groups !== null) {
    const optionalDaysBeginIndex = 4
    const matchesEndIndex = 10
    return {
      type: 'weekly',
      minutes: Number(groups[1]),
      hours: Number(groups[2]),
      days: [groups[3]].concat(
        groups
          .slice(optionalDaysBeginIndex, matchesEndIndex)
          .map(d => d && d.replace(/,/, ''))
          .filter(d => d)
      )
    }
  }
  groups = expression.match(/^(\d+) (\d+) ([\d|L]*) \*\/(\d+) \? \*$/)
  if (groups !== null) {
    return {
      type: 'monthly',
      minutes: Number(groups[1]),
      hours: Number(groups[2]),
      days: groups[3].split(','),
      monthInterval: Number(groups[4])
    }
  }
  return {
    type: 'custom',
    cronExpression: expression
  }
}

const mapWeekDay = (num: number): string => {
  const weekdays = {
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday',
    7: 'Sunday'
  }
  return weekdays[num]
}
